import {EffectiveEntryStatus} from './effective-entry-status'
import type {StackSectionStackEntry} from './merge-box-status-calculator/stack-section-status'
import {type LineTone, LINE_ABOVE_CLASS, LINE_BELOW_CLASS} from './stack-entry-line-tone'

// Each effective entry status maps to a "tone" — a Primer color bucket that
// matches the status icon's color (see the `effective-entry-status-icons`).
// The vertical timeline line segment between two icons is drawn end-to-end in
// the upper entry's tone so the line flowing down from an icon, and the icon
// and label it connects to, all read as the same status (per `line-colors.png`).

const EFFECTIVE_STATUS_TONE: Record<EffectiveEntryStatus, LineTone> = {
  [EffectiveEntryStatus.After]: 'muted',
  [EffectiveEntryStatus.BlockedDownstack]: 'attention',
  [EffectiveEntryStatus.Closed]: 'danger',
  [EffectiveEntryStatus.Draft]: 'muted',
  [EffectiveEntryStatus.Loading]: 'default',
  [EffectiveEntryStatus.MergeConflicts]: 'attention',
  [EffectiveEntryStatus.Merged]: 'done',
  [EffectiveEntryStatus.Merging]: 'done',
  [EffectiveEntryStatus.NeedsRebase]: 'attention',
  [EffectiveEntryStatus.NotReady]: 'attention',
  [EffectiveEntryStatus.Queued]: 'attention',
  [EffectiveEntryStatus.Ready]: 'success',
}

function entryTone(entry: StackSectionStackEntry): LineTone {
  const status = entry.effectiveEntryStatus ?? EffectiveEntryStatus.NotReady
  return EFFECTIVE_STATUS_TONE[status]
}

// Returns the className(s) to apply to a stack entry's `NavList.Item` so its
// vertical line segments above and below the icon match the design, based on
// each entry's `effectiveEntryStatus`.
//
// The line above this entry's icon (the `::before` stub) belongs to — and
// matches the color of — the previous (upper) entry, so the segment between two
// icons reads as one continuous color (the upper entry's). The first entry has
// no line above (hidden via `:first-child::before` in CSS) so the fallback to
// the entry's own tone there is harmless.
export function getEffectiveEntryLineClass(
  entry: StackSectionStackEntry,
  previousEntry?: StackSectionStackEntry,
): string {
  const below = entryTone(entry)
  const above = previousEntry ? entryTone(previousEntry) : below
  return `${LINE_ABOVE_CLASS[above]} ${LINE_BELOW_CLASS[below]}`
}

// Returns the className(s) to apply to the base reference name item (`<li>`) in
// the stack, based on each entry's `effectiveEntryStatus`. Uses the last entry (closest to trunk)
// in the stack to determine the line color above the base ref.
export function getEffectiveBaseRefLineClass(stackEntries: StackSectionStackEntry[]): string {
  if (stackEntries.length === 0) return ''

  const firstEntry = stackEntries[stackEntries.length - 1]

  if (!firstEntry) return ''

  return LINE_ABOVE_CLASS[entryTone(firstEntry)]
}
